import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type {
  ServiceListQuery,
  CreateServiceRequest,
  UpdateServiceRequest,
  AdminService,
} from '../../types/admin.types';

// Query keys for cache management
export const serviceQueryKeys = {
  all: ['admin', 'services'] as const,
  lists: () => [...serviceQueryKeys.all, 'list'] as const,
  list: (params: ServiceListQuery) => [...serviceQueryKeys.lists(), params] as const,
  details: () => [...serviceQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...serviceQueryKeys.details(), id] as const,
};

/**
 * Hook to fetch paginated list of services with filtering
 */
export function useServiceList(params: ServiceListQuery = {}) {
  return useQuery({
    queryKey: serviceQueryKeys.list(params),
    queryFn: () => adminApi.listServices(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch a single service by ID
 */
export function useService(id: string) {
  return useQuery({
    queryKey: serviceQueryKeys.detail(id),
    queryFn: () => adminApi.getService(id),
    enabled: !!id,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to create a new service
 */
export function useCreateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServiceRequest) => adminApi.createService(data),
    onSuccess: () => {
      // Invalidate all service list queries
      queryClient.invalidateQueries({ queryKey: serviceQueryKeys.lists() });
    },
  });
}

/**
 * Hook to update an existing service
 */
export function useUpdateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateServiceRequest }) =>
      adminApi.updateService(id, data),
    onSuccess: (_, variables) => {
      // Invalidate all service list queries
      queryClient.invalidateQueries({ queryKey: serviceQueryKeys.lists() });
      // Also invalidate the specific service detail
      queryClient.invalidateQueries({ queryKey: serviceQueryKeys.detail(variables.id) });
    },
  });
}

/**
 * Hook to delete (deactivate) a service
 */
export function useDeleteService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminApi.deleteService(id),
    onSuccess: () => {
      // Invalidate all service list queries
      queryClient.invalidateQueries({ queryKey: serviceQueryKeys.lists() });
    },
  });
}

/**
 * Hook to toggle service active status
 * Convenience wrapper around useUpdateService for toggling isActive
 */
export function useToggleServiceStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      adminApi.updateService(id, { isActive }),
    onSuccess: (_, variables) => {
      // Invalidate all service list queries
      queryClient.invalidateQueries({ queryKey: serviceQueryKeys.lists() });
      // Also invalidate the specific service detail
      queryClient.invalidateQueries({ queryKey: serviceQueryKeys.detail(variables.id) });
    },
  });
}

/**
 * Helper to get the opposite status for toggling
 */
export function getToggledStatus(service: AdminService): boolean {
  return !service.isActive;
}
